export interface HelpTopic {
  id: string;
  title: string;
  category: string;
  keywords: string[];
  content: string; // markdown-like plain text with ## headings and bullet **bold**
}

export const HELP_CATEGORIES = [
  'Getting Started',
  'Kairos Study',
  'Koinonia Groups',
  'Church Management',
  'Account & Settings',
] as const;

export const HELP_TOPICS: HelpTopic[] = [
  // ─── Getting Started ──────────────────────────────────────────────────────
  {
    id: 'getting-started',
    title: 'Welcome to Kairos',
    category: 'Getting Started',
    keywords: ['welcome', 'start', 'overview', 'what is kairos', 'intro'],
    content: `## What is Kairos?

Kairos is a scripture-first Bible study and church community platform. It has two main areas:

**Kairos Study** — Personal and guided Bible reading through four different reading paths.

**Kairos Groups** — Small-group ministry tools for churches, including group meetings, check-ins, announcements, and a pastor dashboard.

## How to navigate

Use the top navigation bar to switch between sections:
- **Daily** — Today's scheduled reading
- **Journey** — Your personal study path
- **Choose** — Free-select any passage
- **Lectionary** — Follow the church calendar
- **Groups** — Koinonia Groups and church community

## Language
Use the language selector in the header to receive Theophilos insights in **English**, **Afrikaans**, or **isiZulu**.`,
  },

  {
    id: 'signing-in',
    title: 'Signing In & Creating an Account',
    category: 'Getting Started',
    keywords: ['sign in', 'login', 'account', 'register', 'sign up', 'create account', 'password', 'email'],
    content: `## Creating an account

1. Open Kairos in your browser.
2. Click **Create Free Account** on the sign-in page.
3. Enter your email address and a password.
4. Check your inbox for a confirmation email and click the link.

## Signing in

Enter your email and password on the sign-in page and click **Sign In**.

## Guest access

You can tap **Continue as Guest** to explore Kairos Study without an account. Guest sessions do not save your preferences or group memberships.

## Forgot password

On the sign-in page, click **Forgot password?** and enter your email. A reset link will be sent to your inbox.`,
  },

  {
    id: 'onboarding',
    title: 'First-Time Setup (Onboarding)',
    category: 'Getting Started',
    keywords: ['onboarding', 'setup', 'first time', 'profile', 'denomination', 'tradition'],
    content: `## What is onboarding?

The first time you sign in, Kairos walks you through a short setup to personalise your experience. This covers:

- **Your name** — used in groups and personalised content.
- **Denomination / tradition** — helps Kairos tailor theological framing.
- **Language preference** — English, Afrikaans, or isiZulu.
- **Notification preferences** — optional.

## Changing your preferences later

Use the **language selector** in the top navigation bar at any time to switch languages. Other profile settings can be updated through your account menu.`,
  },

  // ─── Kairos Study ─────────────────────────────────────────────────────────
  {
    id: 'daily-reading',
    title: 'Daily Reading',
    category: 'Kairos Study',
    keywords: ['daily', 'passage', 'reading', 'scripture', 'verse', 'today', 'background', 'insight'],
    content: `## What is Daily Reading?

The **Daily** page shows a scheduled scripture passage for today. Each day rotates through Old Testament, Psalm, and New Testament streams.

## How it works

1. Navigate to **Daily** in the top menu.
2. A background card gives historical and literary context for the passage.
3. The scripture card shows the full text. Long passages can be expanded.
4. Below the passage are **Theophilos-generated study insights** — tap any insight card to read it fully.

## Refreshing

Click **Refresh Daily** at the top of the page to reload the passage and regenerate insights. Insights are cached per passage so re-loading is fast.

## Tier access

- **Free** — passage + basic insights.
- **Paid / Pro / Scholar** — extended Theophilos commentary and deeper reflection prompts.`,
  },

  {
    id: 'journey',
    title: 'Journey — Personal Study Path',
    category: 'Kairos Study',
    keywords: ['journey', 'curated', 'book study', 'topic study', 'study mode', 'progress', 'chapter'],
    content: `## What is Journey?

Journey gives you a structured, ongoing reading path with six guided reflection questions for each passage. Your progress is saved.

## Choosing a mode

When you first open Journey, choose one of three modes:

- **Curated Daily** — A fresh, varied passage each day selected from across the whole Bible.
- **Book Study** — Read one book of the Bible cover-to-cover, one chapter per session. Progress is automatically saved.
- **Topic Study** — Explore a theme (e.g. faith, prayer, suffering) through curated passages.

You can also set your preferred **time of day** (morning, midday, evening) to adjust the tone of the reflection.

## Changing modes

Click **Change Mode** at the top of the Journey page to start a different mode at any time. Your previous progress is retained.

## Six reflection questions

Each session includes six pastoral questions covering:
1. What does this text say?
2. What does it mean in its original context?
3. What does it mean for the Church today?
4. What does it mean for me personally?
5. What is the challenge or call to action?
6. How can I pray with this text?`,
  },

  {
    id: 'choose',
    title: 'Choose Your Own Passage',
    category: 'Kairos Study',
    keywords: ['choose', 'free', 'passage', 'reference', 'any verse', 'search', 'notes', 'recent', 'theological'],
    content: `## What is Choose Your Own?

The **Choose** page lets you look up any Bible passage by reference (e.g. "John 3:16", "Romans 8:1-8", "Psalm 23") and receive Theophilos-assisted study commentary.

## Looking up a passage

1. Type a reference in the search box — Kairos will auto-correct minor typos.
2. The scripture card shows the full text with an expand option for long passages.
3. **Theological questions** appear below the passage — click any to get a detailed Theophilos answer.

## Notes

You can type personal notes in the notes field for each passage. Notes are saved locally in your browser.

## Recent passages

Your last 8 passages are listed under **Recent** for quick re-access.`,
  },

  {
    id: 'lectionary',
    title: 'Lectionary (Liturgical Calendar)',
    category: 'Kairos Study',
    keywords: ['lectionary', 'liturgical', 'calendar', 'RCL', 'church year', 'sunday', 'season', 'advent', 'lent', 'easter', 'pentecost', 'thematic'],
    content: `## What is the Lectionary?

The **Lectionary** (also called Liturgical) page follows the **Revised Common Lectionary (RCL)** — the three-year cycle of Sunday readings used by many churches worldwide.

## How to use it

1. Navigate to **Lectionary** in the top menu.
2. The current Sunday's readings are shown automatically.
3. Use the date picker to jump to any Sunday in the liturgical year.
4. Select individual readings (First Reading, Psalm, Second Reading, Gospel) to view and study them.

## Thematic Thread

Click **Thematic Thread** to receive a Theophilos-generated reflection connecting all four readings of the week into a single theme.

## Scholar Notes

The Lectionary includes a notes field for each Sunday — useful for sermon preparation.

## Liturgical seasons

The app recognises Advent, Christmas, Epiphany, Lent, Holy Week, Easter, and Ordinary Time, with appropriate framing for each season.`,
  },

  {
    id: 'theological-questions',
    title: 'Theological Questions',
    category: 'Kairos Study',
    keywords: ['theological', 'questions', 'ask', 'doctrine', 'theology', 'AI', 'panel'],
    content: `## What are Theological Questions?

On the **Choose** and **Lectionary** pages, a **Theological Questions** panel appears below the scripture. These are pre-formed questions relevant to the passage, covering doctrine, ethics, history, and application.

## How to use them

1. Click any question in the list to expand it.
2. A Theophilos response streams in below the question.
3. Click the same question again to collapse it.
4. Responses are cached — reopening a question does not re-generate it.

## Asking your own question

Type a custom question in the input field at the bottom of the panel and press **Ask**. Your question is answered in context of the currently selected passage.`,
  },

  // ─── Koinonia Groups ────────────────────────────────────────────────────────
  {
    id: 'groups-overview',
    title: 'Koinonia Groups — Overview',
    category: 'Koinonia Groups',
    keywords: ['groups', 'koinonia groups', 'community', 'small group', 'bible study', 'fellowship'],
    content: `## What are Koinonia Groups?

Koinonia Groups are small communities within your church focused on Bible study, prayer, and deeper discipleship. Each group can have its own meetings, discussions, announcements, and health check-ins.

## Getting into Groups

When you first visit **Groups**, a setup wizard runs if you are not yet connected to a church:

- **I'm a pastor or church leader** — register your church and receive an invite code.
- **I have an invite code** — enter the code your pastor shared with you.

Once connected to a church, you can browse, join, and create groups.

## Roles in groups

- **Pastor** — manages the whole church and all groups.
- **Coordinator** — assists the pastor; manages group requests.
- **Leader** — leads a specific group, schedules meetings, runs check-ins.
- **Member** — participates in a group.`,
  },

  {
    id: 'joining-a-group',
    title: 'Joining a Koinonia Group',
    category: 'Koinonia Groups',
    keywords: ['join', 'browse', 'find group', 'invite', 'request', 'approval', 'public', 'private'],
    content: `## Browsing groups

Go to **Groups → Browse** (or **Church Groups**) to see all groups in your church.

## Joining a public group

Click on any group and select **Join Group**. For public groups, you are added immediately.

## Joining a private group

Private groups require approval from the group leader. After you request to join, the leader will see your request and can accept or decline it.

## Joining via invite link

Group leaders can share a direct invite link. Clicking the link takes you straight to the group's join page. If you don't have an account yet, you'll be prompted to sign in first.`,
  },

  {
    id: 'creating-a-group',
    title: 'Creating a Koinonia Group',
    category: 'Koinonia Groups',
    keywords: ['create group', 'new group', 'start group', 'request group', 'leader', 'group type'],
    content: `## Who can create a group?

Any member of a church can **request** a new group. The request goes to the church coordinator or pastor for approval.

## How to create a group

1. Go to **Groups → Create** (or click **Create New Group**).
2. Fill in the group name, description, and meeting type (in-person / online / hybrid).
3. Choose the group type (e.g. Bible Study, Prayer, Youth, etc.).
4. Set privacy — **Public** (anyone in the church can join) or **Private** (approval required).
5. Submit the request.

## After the request is submitted

A coordinator or pastor reviews the request. Once approved, you become the group leader and the group appears in the Browse list.`,
  },

  {
    id: 'group-detail',
    title: 'Inside a Group — Meetings, Comments & Announcements',
    category: 'Koinonia Groups',
    keywords: ['meetings', 'comments', 'announcements', 'rsvp', 'going', 'maybe', 'group page', 'discussion'],
    content: `## Group tabs

Each group page has tabs for different content:

- **About** — Group description, type, meeting schedule.
- **Meetings** — Upcoming and past meetings with RSVP.
- **Discussion** — Members can post comments and reply to one another.
- **Announcements** — Leaders post news and reminders to the whole group.

## RSVP to a meeting

On the Meetings tab, click a meeting and choose **Going**, **Maybe**, or **Not Going**. The leader can see who is attending.

## Posting a comment

Go to the Discussion tab, type in the comment box, and click **Post**. All group members can see and respond.

## Sharing a group invite

Leaders and members can copy the group's invite link from the group page header. Share it via WhatsApp or any other channel.`,
  },

  {
    id: 'health-checkin',
    title: 'Group Health Check-Ins',
    category: 'Koinonia Groups',
    keywords: ['health', 'check-in', 'checkin', 'wellbeing', 'leader', 'spiritual health', 'pulse'],
    content: `## What is a health check-in?

Health check-ins allow group leaders to take a regular pulse of their group's spiritual and relational health. Members respond to a short set of questions.

## For leaders

Go to your group and click **Health Check-In** (or navigate to **Leader Dashboard → Health Check-in**). You can review aggregate responses from your group members and identify members who may need extra support.

## For members

When a check-in is active, you'll be prompted within your group page to complete it. The check-in takes 1–2 minutes and covers areas such as engagement, personal growth, and fellowship.`,
  },

  {
    id: 'my-groups',
    title: 'My Groups',
    category: 'Koinonia Groups',
    keywords: ['my groups', 'joined', 'membership', 'leave group', 'active groups'],
    content: `## Viewing your groups

Navigate to **Groups → My Groups** to see all groups you belong to. Each card shows your role (member, leader) and links directly to the group.

## Leaving a group

Open the group detail page and find the **Leave Group** option. Leaving a group removes you from meetings, announcements, and discussions for that group.

## Multiple groups

You can be a member of multiple groups at the same time. The home page shows your first four groups for quick access.`,
  },

  // ─── Church Management ───────────────────────────────────────────────────
  {
    id: 'church-setup',
    title: 'Setting Up Your Church',
    category: 'Church Management',
    keywords: ['church', 'setup', 'register', 'pastor', 'invite code', 'create church'],
    content: `## Who should set up a church?

The **lead pastor or church administrator** should register the church. This creates the church profile and generates the first invite code.

## How to register

1. Go to **Groups** for the first time.
2. The onboarding wizard appears — choose **I'm a pastor or church leader**.
3. Fill in the church name (required), denomination, city, and country (optional).
4. Click **Create Church**.
5. Your invite code and share link are displayed immediately.

## Sharing the invite

- Copy the **invite code** and share it verbally or by text.
- Copy the **invite link** and share it on WhatsApp, email, or any other channel.
- Click **Share on WhatsApp** to open WhatsApp with a pre-filled message.

## What happens when members join

Members enter the invite code or click the link. They fill in their details. When they sign up for Kairos with the same email, they are automatically matched to your church.`,
  },

  {
    id: 'pastor-dashboard',
    title: 'Pastor Dashboard',
    category: 'Church Management',
    keywords: ['pastor dashboard', 'dashboard', 'church members', 'coordinator', 'group requests', 'health overview', 'unconnected'],
    content: `## Opening the dashboard

Go to **Groups → Dashboard** (or **Leader Tools**). Only pastors and coordinators can see all sections.

## Church Settings panel

Click **Church Settings** to expand:
- View and copy the church **invite code** and **share link**.
- Share directly to WhatsApp.
- Toggle **Require approval for new members** on or off.
- See the full **member list** with roles.
- Assign or remove **Coordinator** role from members.

## Importing members from a file

Inside Church Settings, click **Import from file** next to the member list. Upload a **.csv**, **.xlsx**, or **.xls** file with columns: **Name**, **Email**, and optionally **Phone/Cell**. Members are added to Pending Invites and matched automatically when they sign up.

## Group Requests

New group creation requests from members appear here. Approve or decline each request.

## Group Health Overview

See an aggregate health summary across all groups in your church.

## Unconnected Members

A counter shows how many church members are not in any group. Click **Reach out →** to see who they are.

## Pending Invites

Members who were invited via the invite code or imported from a file but have not yet created a Kairos account appear here.`,
  },

  {
    id: 'invite-code',
    title: 'Church Invite Codes & Links',
    category: 'Church Management',
    keywords: ['invite code', 'invite link', 'share', 'whatsapp', 'join church', 'code'],
    content: `## What is an invite code?

An invite code is a short (6-character) code that identifies your church. Anyone who enters it can join your church community in Kairos.

## Where to find your code

Open **Groups → Dashboard → Church Settings**. The invite code and full share link are shown at the top.

## Sharing options

- **Copy code** — Share verbally or paste into a message.
- **Copy link** — Full URL like \`https://kairos.app/church-invite/ABC123\`.
- **Share on WhatsApp** — Opens WhatsApp with a pre-filled invitation message.

## What the recipient sees

Clicking the link opens a landing page where the person fills in their name, email, and optional phone number. They are then added to **Pending Invites**. When they sign up for Kairos using the same email, they are automatically connected to your church.

## Changing the invite code

Currently the invite code is set when the church is created. Contact support if you need to reset it.`,
  },

  {
    id: 'member-import',
    title: 'Importing Members from a File',
    category: 'Church Management',
    keywords: ['import', 'csv', 'excel', 'xlsx', 'spreadsheet', 'bulk', 'upload members', 'congregation list'],
    content: `## Who can import members?

Only **pastors** can use the bulk member import feature from the Pastor Dashboard.

## Supported file formats

- **.csv** — comma-separated values (works from Excel, Google Sheets, etc.)
- **.xlsx** — Excel workbook
- **.xls** — older Excel format

## Required columns

Your file must contain at least these two columns (the header name is flexible):

| Column | Accepted headers |
|--------|-----------------|
| Name   | Name, Full Name, Full_Name |
| Email  | Email, Email Address, E-mail |

**Optional:** Phone, Cell, Mobile, Phone Number

## How to import

1. Open **Groups → Dashboard → Church Settings**.
2. Click **Import from file** next to the member count.
3. Drag and drop your file, or click to browse.
4. Review the preview — valid rows are shown in white, rows with errors are shown in red.
5. Click **Import X members** to confirm.

## What happens after import

Imported members appear in **Pending Invites**. When they sign up for Kairos with their email address, they are automatically matched to your church.

## Example CSV

\`\`\`
Name,Email,Phone
John Smith,john@church.org,0821234567
Jane Doe,jane@church.org,
\`\`\``,
  },

  {
    id: 'coordinator-role',
    title: 'Coordinator Role',
    category: 'Church Management',
    keywords: ['coordinator', 'role', 'assign', 'permissions', 'assistant pastor', 'admin'],
    content: `## What can a coordinator do?

A coordinator is a trusted assistant to the pastor. They can:
- Approve or decline group creation requests.
- View the full church member list.
- Access the Pastor Dashboard.
- Manage group health overviews.

## Assigning a coordinator

1. Open **Groups → Dashboard → Church Settings**.
2. Find the member in the member list.
3. Click **Make Coordinator** next to their name.
4. They will immediately have coordinator access.

## Removing coordinator access

Find the same member in the list and click **Remove Coordinator** to return them to a regular member role.`,
  },

  {
    id: 'pending-invites',
    title: 'Pending Invites',
    category: 'Church Management',
    keywords: ['pending', 'invites', 'not yet signed up', 'invited', 'waiting', 'pre-registered'],
    content: `## What are pending invites?

Pending invites are people who have been added to your church (via invite link, guest join, or bulk import) but have not yet created a Kairos account.

## Viewing pending invites

Open **Groups → Dashboard → Church Settings** and scroll to the **Pending Invites** section. Each entry shows the name, email, and optional phone number.

## How they become members

When a person signs up for Kairos using the same email address they used when they were invited, they are automatically matched to your church and removed from the pending list.

## Manual follow-up

Use the email or phone details shown to follow up personally with people who have not yet signed up.`,
  },

  // ─── Account & Settings ──────────────────────────────────────────────────
  {
    id: 'tiers',
    title: 'Access Tiers',
    category: 'Account & Settings',
    keywords: ['tier', 'free', 'paid', 'pro', 'scholar', 'subscription', 'upgrade', 'plan', 'features'],
    content: `## Kairos access tiers

Kairos has four access tiers that control the depth of Theophilos-generated content:

| Tier | Description |
|------|-------------|
| **Free** | Core reading + basic insights. Great for getting started. |
| **Paid** | Extended Theophilos commentary and richer reflection prompts. |
| **Pro** | Full access including advanced theological analysis and priority responses. |
| **Scholar** | All Pro features plus tools designed for sermon preparation and academic study. |

## Changing your tier

Use the **Tier** selector in the top-right of the navigation bar to switch tiers. This is currently a manual switch — paid tiers will require a subscription.

## What's gated

Higher tiers unlock deeper Theophilos insights on the Daily, Journey, Choose, and Lectionary pages.`,
  },

  {
    id: 'language',
    title: 'Language Settings',
    category: 'Account & Settings',
    keywords: ['language', 'afrikaans', 'english', 'isizulu', 'zulu', 'translate', 'preference'],
    content: `## Supported languages

Theophilos responses can be delivered in:
- **English** (default)
- **Afrikaans**
- **isiZulu**

## Changing the language

Use the **language selector** in the header bar (below the navigation links). Your selection is saved to your profile and takes effect immediately on the next Theophilos request.

## What is translated?

Theophilos-generated content (insights, reflections, theological answers, thematic threads) is produced in your chosen language. The UI itself remains in English.`,
  },

  {
    id: 'sign-out',
    title: 'Signing Out',
    category: 'Account & Settings',
    keywords: ['sign out', 'logout', 'leave', 'account'],
    content: `## How to sign out

Click your **name or email badge** in the top-right corner of the navigation bar, then click **Sign out**.

You will be returned to the sign-in page. Your data and group memberships are saved to your account.`,
  },
];

/** Search topics by query string — matches title, keywords, category, and content. */
export function searchTopics(query: string): HelpTopic[] {
  if (!query.trim()) return HELP_TOPICS;
  const q = query.toLowerCase().trim();
  return HELP_TOPICS.filter((t) => {
    return (
      t.title.toLowerCase().includes(q) ||
      t.category.toLowerCase().includes(q) ||
      t.keywords.some((k) => k.includes(q)) ||
      t.content.toLowerCase().includes(q)
    );
  });
}
